import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { generateUniqueLinkCode, suggestUsername, hashPassword, sanitizeDisplayName } from '../../../../accounts';
import type { AccountsState } from '../../../../accounts';
interface AddUserFormProps {
  accounts: AccountsState | undefined;
  store: {
    createAccount: (role: string, options: Record<string, unknown>) => void;
  };
  onClose: () => void;
}
const MAX_NAME_LENGTH = 100;
const MIN_NAME_LENGTH = 2;
const MIN_PASSWORD_LENGTH = 4;
export function AddUserForm({
  accounts,
  store,
  onClose
}: AddUserFormProps) {
  const [newUserRole, setNewUserRole] = useState<'learner' | 'teacher' | 'parent' | 'admin'>('learner');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserSchool, setNewUserSchool] = useState('');
  const [newUserGrade, setNewUserGrade] = useState('6');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    const trimmedName = sanitizeDisplayName(newUserName.trim());
    if (trimmedName.length < MIN_NAME_LENGTH) {
      setValidationError(`Naam moet minimaal ${MIN_NAME_LENGTH} tekens zijn.`);
      return;
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      setValidationError(`Naam mag maximaal ${MAX_NAME_LENGTH} tekens zijn.`);
      return;
    }
    if (newUserRole === 'learner') {
      const pw = newUserPassword.trim();
      if (pw && pw.length < MIN_PASSWORD_LENGTH) {
        setValidationError(`Wachtwoord moet minimaal ${MIN_PASSWORD_LENGTH} tekens zijn.`);
        return;
      }
      const linkCode = generateUniqueLinkCode(accounts);
      const username = suggestUsername(trimmedName);
      const passwordHash = pw ? await hashPassword(pw) : undefined;
      store.createAccount(newUserRole, {
        displayName: trimmedName,
        email: newUserEmail || undefined,
        grade: parseInt(newUserGrade, 10),
        linkCode,
        username,
        passwordHash
      });
    } else {
      store.createAccount(newUserRole, {
        displayName: trimmedName,
        email: newUserEmail || undefined,
        schoolName: newUserSchool || undefined
      });
    }
    onClose();
  };
  return <form onSubmit={handleAddUser} className="space-y-4" data-id="element-5056">
      {validationError && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-id="element-5057">
          {validationError}
        </div>}
      <Select label="Rol" value={newUserRole} onChange={e => setNewUserRole(e.target.value as 'learner' | 'teacher' | 'parent' | 'admin')} options={[{
      value: 'learner',
      label: 'Leerling'
    }, {
      value: 'teacher',
      label: 'Docent'
    }, {
      value: 'parent',
      label: 'Ouder'
    }, {
      value: 'admin',
      label: 'Admin'
    }]} data-id="element-5058" />
      <Input label="Naam" type="text" required value={newUserName} onChange={e => setNewUserName(e.target.value)} placeholder="Volledige naam" data-id="element-5059" />
      <Input label="E-mailadres (optioneel)" type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder="email@voorbeeld.nl" data-id="element-5060" />
      {newUserRole === 'teacher' && <Input label="School" type="text" value={newUserSchool} onChange={e => setNewUserSchool(e.target.value)} placeholder="Naam van de school" data-id="element-5061" />}
      {newUserRole === 'learner' && <Select label="Groep" value={newUserGrade} onChange={e => setNewUserGrade(e.target.value)} options={[{
      value: '6',
      label: 'Groep 6'
    }, {
      value: '7',
      label: 'Groep 7'
    }, {
      value: '8',
      label: 'Groep 8'
    }]} data-id="element-5062" />}
      {newUserRole === 'learner' && <Input label="Wachtwoord (optioneel)" type="password" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} placeholder="Minimaal 4 tekens" minLength={4} icon={<KeyRound className="w-4 h-4" data-id="element-5064" />} helperText="Leerling kan ook inloggen met koppelcode (wordt automatisch gegenereerd)." data-id="element-5063" />}
      <div className="pt-4 flex justify-end gap-2" data-id="element-5065">
        <Button type="button" variant="secondary" onClick={onClose} data-id="element-5066">
          Annuleren
        </Button>
        <Button type="submit" data-id="element-5067">Aanmaken</Button>
      </div>
    </form>;
}